import { StateType } from 'src/redux/reducers';
import { MatchControl } from 'src/types/matchControl';

import { DataSourceAdapter, SuggestionOption, DomainShortcut, SuggestionCategory } from '../types';
import { createSearchableText } from '../utils';

export class MatchControlDataSource implements DataSourceAdapter<MatchControl> {
  readonly type = 'matchControl' as const;

  // Define domain-specific shortcuts for match control
  private readonly domainShortcuts: DomainShortcut[] = [
    {
      prefix: '!a',
      description: 'Age - Player age information',
      template: 'Age: ${age}',
      applicableCategories: ['Members'],
      categoryPrefix: 'Age',
    },
    {
      prefix: '!n',
      description: 'Name - Player name with team',
      template: '${name} (${shortTeamName})',
      applicableCategories: ['Members'],
      categoryPrefix: 'Name',
    },
    {
      prefix: '!c',
      description: 'Caps - Total caps information',
      template: 'Total caps: ${caps}',
      applicableCategories: ['Members'],
      categoryPrefix: 'Caps',
    },
    {
      prefix: '!m',
      description: 'Matches - EHC 2025 matches',
      template: 'EHC 2025 Matches: ${matches}',
      applicableCategories: ['Members'],
      categoryPrefix: 'Matches',
    },
    {
      prefix: '!g',
      description: 'Goals - EHC 2025 goals',
      template: 'EHC 2025: ${goals} Goals',
      applicableCategories: ['Members'],
      categoryPrefix: 'Goals',
    },
  ];

  isAvailable(state: unknown): boolean {
    const typedState = state as StateType;
    return !!typedState.matchControls?.matchControl?.data;
  }

  extractData(state: unknown): MatchControl | null {
    const typedState = state as StateType;
    return typedState.matchControls?.matchControl?.data || null;
  }

  /**
   * SIMPLIFIED: Process domain shortcut queries (!a, !n, !c, !m, !g)
   * This replaces the complex routing and filtering with a direct approach
   */
  processSpecialCharacterQuery(
    query: string,
    matchControl: MatchControl | null
  ): SuggestionOption[] {
    if (!matchControl || !query?.startsWith('!') || query.length < 2) {
      return [];
    }

    // Validate matchControl structure
    if (!Array.isArray(matchControl.contestants)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('MatchControl contestants is not an array:', matchControl.contestants);
      }
      return [];
    }

    const shortcutPrefix = query.substring(0, 2);
    const searchQuery = query.substring(2).trim().toLowerCase();

    // Find the matching shortcut
    const shortcut = this.domainShortcuts.find((s) => s.prefix === shortcutPrefix);
    if (!shortcut) return [];

    // Get all members from all contestants
    const allMembers: Array<{
      member: {
        id?: string;
        name: string;
        number?: string;
        [key: string]: unknown;
      };
      contestant: {
        id?: string;
        name?: string;
        shortName?: string;
        [key: string]: unknown;
      };
      label: string;
      value: string;
    }> = [];

    matchControl.contestants.forEach((contestant) => {
      // Validate contestant structure
      if (!contestant || typeof contestant !== 'object') {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Invalid contestant:', contestant);
        }
        return;
      }

      if (!Array.isArray(contestant.members)) {
        // Some contestants might not have members, this is okay
        return;
      }

      contestant.members.forEach((member) => {
        // Validate member structure
        if (!member || typeof member !== 'object' || !member.name) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Invalid member:', member);
          }
          return;
        }

        const label =
          member.number !== '' && member.number !== undefined
            ? `#${member.number} | ${member.name} (${contestant.name || contestant.shortName})`
            : `${member.name} (${contestant.name || contestant.shortName})`;

        allMembers.push({
          member,
          contestant,
          label,
          value: `matchControl-member-${member.id || member.name}`,
        });
      });
    });

    // Filter members based on search query if provided
    let filteredMembers = allMembers;
    if (searchQuery) {
      filteredMembers = allMembers.filter(({ member, label }) => {
        // Simple string matching - check if search term appears in name or label
        const searchText = `${member.name} ${label}`.toLowerCase();
        return searchText.includes(searchQuery);
      });
    }

    // Transform to domain shortcut suggestions
    return filteredMembers.map(({ member, contestant, label, value }) => {
      // Generate the actual value based on the shortcut template
      const templateValue = this.generateTemplateValue(shortcut, member, contestant);

      return {
        value,
        label: `${shortcut.prefix} ${label}`,
        category: `${shortcut.categoryPrefix} - Members` as SuggestionCategory,
        source: 'matchControl' as const,
        data: {
          ...member,
          contestant,
          domainShortcut: shortcut.prefix,
          domainShortcutValue: templateValue, // This is the value that should be inserted
        },
        searchableText: createSearchableText(member),
      };
    });
  }

  /**
   * Generate value based on domain shortcut template
   */
  private generateTemplateValue(shortcut: DomainShortcut, member: any, contestant: any): string {
    const template = shortcut.template;

    // Create context object with available variables
    const context = {
      name: member.name || '',
      age: member.age || 'Unknown',
      caps: member.caps || '0',
      matches: member.matches || '0',
      goals: member.goals || '0',
      shortTeamName: contestant.shortName || contestant.name || 'Team',
      teamName: contestant.name || contestant.shortName || 'Team',
      number: member.number || '',
    };

    // Simple template replacement
    let result = template;
    Object.entries(context).forEach(([key, val]) => {
      const placeholder = `\${${key}}`;
      result = result.replace(
        new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
        String(val)
      );
    });

    return result;
  }

  process(matchControl: MatchControl | null): SuggestionOption[] {
    if (!matchControl) return [];

    const options: SuggestionOption[] = [];

    // Add contestants
    if (matchControl.contestants.length) {
      matchControl.contestants.forEach((contestant, index) => {
        options.push({
          value: `matchControl-contestant-${contestant.id}`,
          label: `${contestant.name || contestant.shortName || `Team ${index + 1}`}`,
          category: 'Teams' as const,
          source: 'matchControl',
          data: contestant,
          searchableText: createSearchableText(contestant),
        });

        // Add member data if available
        if (contestant.members?.length) {
          contestant.members.forEach((member) => {
            const label =
              member.number !== ''
                ? `#${member.number} | ${member.name} (${contestant.name || contestant.shortName})`
                : `${member.name} (${contestant.name || contestant.shortName})`;

            options.push({
              value: `matchControl-member-${member.id || member.name}`,
              label,
              category: 'Members' as const,
              source: 'matchControl',
              data: { ...member, contestant },
              searchableText: createSearchableText(member),
            });
          });
        }
      });
    }

    // Add score data
    if ('score' in matchControl && matchControl.score) {
      options.push({
        value: `matchControl-score`,
        label: `Score: ${JSON.stringify(matchControl.score)}`,
        category: 'Score' as const,
        source: 'matchControl',
        data: matchControl.score,
        searchableText: createSearchableText(matchControl.score),
      });
    }

    return options;
  }
}
